import React from "react"
import {useParams} from "react-router-dom"
import {useAccessToken, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {
	getApplicationForm,
	getApplicationFormClient,
	ApplicationFormDetail,
	ApplicationFormDetailV2,
	isApplicationFormDetailV2,
} from "../../resources/applicationForm"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import HorizontalField, {OptionalHorizontalField} from "../../components/HorizontalField/HorizontalField"
import moment from "moment"
import ReactJson from "react-json-view"
import Card from "../../containers/Card/Card"
import TagsViewer from "../../components/Tags/TagsViewer"

function ApplicationFormDetailsV1({applicationForm}: {applicationForm: ApplicationFormDetail}) {
	if (applicationForm.type !== "applicationForm") return null
	const attrs = applicationForm.attributes
	return (
		<>
			<div className="columns">
				<div className="column">
					<Card title="Application Form Details">
						<HorizontalField label="ID">
							<input type="text" readOnly value={applicationForm.id} className="input is-static" />
						</HorizontalField>
						<HorizontalField label="URL">
							<a href={attrs.url} target="_blank" rel="noopener noreferrer" className="is-static">
								{attrs.url}
							</a>
						</HorizontalField>
						<OptionalHorizontalField title="Organization ID" attribute={applicationForm.relationships?.org?.data?.id} />
						<HorizontalField label="Created At">
							<input
								type="text"
								readOnly
								value={moment(attrs.createdAt).format("MMM DD, YYYY HH:mm:ss")}
								className="input is-static"
							/>
						</HorizontalField>
						<HorizontalField label="Updated At">
							<input
								type="text"
								readOnly
								value={moment(attrs.updatedAt).format("MMM DD, YYYY HH:mm:ss")}
								className="input is-static"
							/>
						</HorizontalField>
						{attrs.settingsOverride?.redirectUrl && (
							<OptionalHorizontalField title="Redirect URL" attribute={attrs.settingsOverride.redirectUrl} />
						)}
						{attrs.settingsOverride?.privacyPolicyUrl && (
							<OptionalHorizontalField title="Privacy Policy URL" attribute={attrs.settingsOverride.privacyPolicyUrl} />
						)}
					</Card>
				</div>
			</div>
			{attrs.tags && Object.keys(attrs.tags).length > 0 && (
				<div className="columns">
					<div className="column">
						<Card title="Tags">
							<TagsViewer tags={attrs.tags} />
						</Card>
					</div>
				</div>
			)}
		</>
	)
}

function ApplicationFormDetailsV2({applicationForm}: {applicationForm: ApplicationFormDetailV2}) {
	const attrs = applicationForm.attributes
	const settings = attrs.applicationFormSettings

	return (
		<>
			<div className="columns">
				<div className="column">
					<Card title="Application Form Details">
						<HorizontalField label="ID">
							<input type="text" readOnly value={applicationForm.id} className="input is-static" />
						</HorizontalField>
						<HorizontalField label="Created At">
							<input
								type="text"
								readOnly
								value={moment(attrs.createdAt).format("MMM DD, YYYY HH:mm:ss")}
								className="input is-static"
							/>
						</HorizontalField>
						<HorizontalField label="Updated At">
							<input
								type="text"
								readOnly
								value={moment(attrs.updatedAt).format("MMM DD, YYYY HH:mm:ss")}
								className="input is-static"
							/>
						</HorizontalField>
						<HorizontalField label="Token Expiration">
							<input
								type="text"
								readOnly
								value={moment(attrs.applicationFormToken.expiration).format("MMM DD, YYYY HH:mm:ss")}
								className="input is-static"
							/>
						</HorizontalField>
						{attrs.requestedProducts && attrs.requestedProducts.length > 0 && (
							<HorizontalField label="Requested Products">
								<input type="text" readOnly value={attrs.requestedProducts.join(", ")} className="input is-static" />
							</HorizontalField>
						)}
						<OptionalHorizontalField title="Is Unified" attribute={attrs.isUnified?.toString()} />
					</Card>
				</div>
			</div>
			{settings && (
				<div className="columns">
					<div className="column">
						<Card title="Application Form Settings">
							<OptionalHorizontalField title="Brand Name" attribute={settings.brandName} />
							<OptionalHorizontalField title="Privacy Policy URL" attribute={settings.privacyPolicyUrl} />
							<OptionalHorizontalField
								title="Electronic Disclosures URL"
								attribute={settings.electronicDisclosuresUrl}
							/>
							<OptionalHorizontalField title="Deposit Terms URL" attribute={settings.depositTermsUrl} />
							<OptionalHorizontalField title="Contact Us Email" attribute={settings.contactUsEmail} />
							<OptionalHorizontalField
								title="Validate Phone Number"
								attribute={settings.validatePhoneNumber?.toString()}
							/>
							<OptionalHorizontalField title="Redirect URL" attribute={settings.redirectUrl} />
							<OptionalHorizontalField title="Redirect Button Text" attribute={settings.redirectButtonText} />
							<OptionalHorizontalField
								title="Use Selfie Verification"
								attribute={settings.useSelfieVerification?.toString()}
							/>
							<OptionalHorizontalField
								title="Show Electronic Disclosure Stage"
								attribute={settings.showElectronicDisclosureStage?.toString()}
							/>
							<OptionalHorizontalField title="Show Tax Stage" attribute={settings.showTaxStage?.toString()} />
							{settings.additionalDisclosures && settings.additionalDisclosures.length > 0 && (
								<HorizontalField label="Additional Disclosures">
									<input
										type="text"
										readOnly
										value={JSON.stringify(settings.additionalDisclosures)}
										className="input is-static"
									/>
								</HorizontalField>
							)}
						</Card>
					</div>
				</div>
			)}
			{attrs.tags && Object.keys(attrs.tags).length > 0 && (
				<div className="columns">
					<div className="column">
						<Card title="Tags">
							<TagsViewer tags={attrs.tags} />
						</Card>
					</div>
				</div>
			)}
		</>
	)
}

function ApplicationFormClientPanel({id, presignedUrlToken}: {id: string; presignedUrlToken?: string}) {
	const accessToken = useAccessToken()
	const result = useAsyncResult(
		() => getApplicationFormClient(accessToken, id, presignedUrlToken),
		[id, presignedUrlToken]
	)

	return (
		<div className="columns">
			<div className="column">
				<Card title="Application Form Raw Data">
					<AsyncResultComponent asyncResult={result} pendingComponent={<div className="loading">Loading...</div>}>
						{({value}) => <ReactJson src={value as object} displayDataTypes={false} />}
					</AsyncResultComponent>
				</Card>
			</div>
		</div>
	)
}

export default function ApplicationFormPage() {
	const {id} = useParams<{id: string}>()
	const accessToken = useAccessToken()
	const isUnitUser = useIsUnitUser()

	if (!isUnitUser || !id) {
		return null
	}

	const result = useAsyncResult(() => getApplicationForm(accessToken, id), [id])

	return (
		<>
			<AsyncResultComponent asyncResult={result}>
				{({value}) => {
					const applicationForm = value.data
					const presignedUrlToken = isApplicationFormDetailV2(applicationForm)
						? applicationForm.attributes.applicationFormToken.token
						: undefined
					const applicationId = applicationForm.relationships?.application?.data?.id
					const meta: MetaItemProps[] = applicationId
						? [{text: "Application", icon: "user-id-card", path: `/applications/${applicationId}`}]
						: []
					return (
						<>
							<TitleBar title={`Application Form ${id}`} meta={meta} />
							<MainSection>
								<div className="columns application-form-page">
									<div className="column is-4">
										{isApplicationFormDetailV2(value.data) ? (
											<ApplicationFormDetailsV2 applicationForm={value.data} />
										) : (
											<ApplicationFormDetailsV1 applicationForm={value.data} />
										)}
									</div>
									<div className="column is-8 application-form-right-column">
										{id ? <ApplicationFormClientPanel id={id} presignedUrlToken={presignedUrlToken} /> : null}
									</div>
								</div>
							</MainSection>
						</>
					)
				}}
			</AsyncResultComponent>
		</>
	)
}
